import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import {
  Dialog,
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  FormControl,
  Select,
  MenuItem,
  CircularProgress,
  Divider,
  Switch,
  FormControlLabel,
} from "@mui/material";
import {
  Close as IconClose,
  Send as IconSend,
  AutoAwesome as AutoAwesomeIcon,
  Add as AddIcon,
} from "@mui/icons-material";
import { apiSendEmailToCategory, apiGetContactsCollection } from "../../../graphQL/addressbook/collections/contacts";
import { apiCreateJournal } from "../../../graphQL/journal";
import { apiGetDomainByName } from "../../../graphQL/domain";
import { OPEN_GLOBAL_REMINDER_EVENT } from "../../../utils/constants";
import UniversalActionButton, { UNIVERSAL_BUTTON_STYLES } from "../../../components/common/UniversalActionButton";

const INITIAL_SIGNATURE_BLOCKS = [{ key: "signature_1", label: "Signature 1", value: "" }];

const sanitizeSignatureKey = (value, fallback = "signature_1") => {
  const raw = String(value || "").trim().toLowerCase();
  const cleaned = raw.replace(/[^a-z0-9_]+/g, "_").replace(/^_+|_+$/g, "");
  return cleaned || fallback;
};

const normalizeSignatureBlocks = (value) => {
  if (!Array.isArray(value) || value.length === 0) return INITIAL_SIGNATURE_BLOCKS;
  const used = new Set();
  const cleaned = value
    .map((item, idx) => {
      const baseKey = sanitizeSignatureKey(item?.key, `signature_${idx + 1}`);
      let key = baseKey;
      let n = 2;
      while (used.has(key)) {
        key = `${baseKey}_${n}`;
        n += 1;
      }
      used.add(key);
      const label = String(item?.label || `Signature ${idx + 1}`).trim();
      const body = String(item?.value || "");
      return { key, label, value: body };
    })
    .filter((item) => item.key);
  return cleaned.length ? cleaned : INITIAL_SIGNATURE_BLOCKS;
};

// Normalize an incoming "recipient" entry into a raw contact object.
// Accepts: raw contact ({ id, identity, reach, ... }),
//          contact-card with `_raw` ({ ..., _raw: rawContact }),
//          or a flat card with firstName/lastName/email fields.
const toRawContact = (entry) => {
  if (!entry || typeof entry !== "object") return null;
  if (entry._raw && typeof entry._raw === "object") return entry._raw;
  if (entry.identity || entry.reach) return entry;
  // Flat card fallback: synthesize a minimal raw contact.
  return {
    id: entry.id || entry.contactId || null,
    identity: {
      person: { firstName: entry.firstName || "", lastName: entry.lastName || "" },
      company: { name: entry.company || "" },
    },
    reach: {
      emails: entry.email ? [{ value: entry.email }] : [],
    },
    state: entry.state || "",
    domain: entry.domain || "",
  };
};

const contactHasEmail = (c) => {
  const emails = Array.isArray(c?.reach?.emails) ? c.reach.emails : [];
  return emails.some((e) => e?.value && String(e.value).trim());
};

const firstContactEmail = (c) => {
  const emails = Array.isArray(c?.reach?.emails) ? c.reach.emails : [];
  return String(emails.find((e) => e?.value && String(e.value).trim())?.value || "").trim();
};

const truthyFlag = (value) => value === true || String(value || "").trim().toLowerCase() === "true";

const isDoNotContact = (contact) => {
  const data = contact?.data && typeof contact.data === "object" ? contact.data : {};
  const emailPrefs = data?.email && typeof data.email === "object" ? data.email : {};
  const emails = Array.isArray(contact?.reach?.emails) ? contact.reach.emails : [];
  const state = String(contact?.state || contact?.status || "").trim().toLowerCase();
  return Boolean(
    truthyFlag(contact?.doNotContact)
    || truthyFlag(contact?.do_not_contact)
    || truthyFlag(data?.doNotContact)
    || truthyFlag(data?.do_not_contact)
    || truthyFlag(data?.emailOptOut)
    || truthyFlag(data?.unsubscribed)
    || truthyFlag(emailPrefs?.doNotContact)
    || truthyFlag(emailPrefs?.optOut)
    || truthyFlag(emailPrefs?.unsubscribed)
    || emails.some((e) => truthyFlag(e?.doNotContact) || truthyFlag(e?.optOut) || truthyFlag(e?.unsubscribed))
    || ["do-not-contact", "do_not_contact", "unsubscribed", "opted-out", "opted_out"].includes(state)
  );
};

const createDryRunResult = (filtered, sendable, blocked = []) => ({
  totalMatched: filtered.length,
  totalAttempted: sendable.length,
  totalSent: 0,
  totalFailed: 0,
  totalSkipped: filtered.length - sendable.length,
  dryRun: true,
  results: [
    ...sendable.map((contact) => ({
      contactId: contact?.id || contact?._id || "",
      email: firstContactEmail(contact),
      status: "dry-run",
      error: "",
      messageId: "",
    })),
    ...blocked.map((contact) => ({
      contactId: contact?.id || contact?._id || "",
      email: firstContactEmail(contact),
      status: "do-not-contact",
      error: "Skipped because contact is marked do-not-contact or unsubscribed.",
      messageId: "",
    })),
  ],
});

const DEFAULT_EMAIL_WORKFLOW_OPTIONS = {
  compliance: {
    skipDoNotContact: true,
    unsubscribeStatus: "not-configured",
  },
  retryPolicy: {
    maxRetries: 3,
    retryDelayMinutes: 15,
  },
};

const BulkEmailDialog = ({
  open,
  onClose,
  categoryOptions = [],
  defaultCategory = "",
  authorId,
  domain,
  defaultFrom = "",
  recipients = null,
  title,
}) => {
  const usingExplicitRecipients = Array.isArray(recipients);

  const isSuperAdmin = useSelector((state) => {
    const u = state?.auth?.user || {};
    if (u.isSuperAdmin === true) return true;
    const role = String(state?.auth?.PrivateInfo?.role || u.role || "").trim().toLowerCase();
    return role === "superadmin" || role === "sysadmin";
  });

  const [category, setCategory] = useState("");
  const [from, setFrom] = useState("");
  const [replyTo, setReplyTo] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [actuallySendEmails, setActuallySendEmails] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  // Pool: either fetched-by-category items, or the supplied recipients (normalized).
  const [poolLoading, setPoolLoading] = useState(false);
  const [pool, setPool] = useState([]); // array of raw contact objects
  const [followUpAmount, setFollowUpAmount] = useState(3);
  const [followUpUnit, setFollowUpUnit] = useState("day");
  const [followUpText, setFollowUpText] = useState("");
  const [creatingFollowUps, setCreatingFollowUps] = useState(false);
  const [followUpStatus, setFollowUpStatus] = useState("");
  const [creatingBulkEventReminder, setCreatingBulkEventReminder] = useState(false);
  const [signatureBlocks, setSignatureBlocks] = useState(INITIAL_SIGNATURE_BLOCKS);
  const [selectedSignatureKey, setSelectedSignatureKey] = useState("signature_1");
  const [loadingSignatures, setLoadingSignatures] = useState(false);

  // New: in-dialog filters.
  const [stateFilter, setStateFilter] = useState("Any");
  const [domainFilter, setDomainFilter] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [searchFilter, setSearchFilter] = useState("");

  // Debounce search input.
  const searchTimer = useRef(null);
  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => setSearchFilter(searchInput), 250);
    return () => { if (searchTimer.current) clearTimeout(searchTimer.current); };
  }, [searchInput]);

  useEffect(() => {
    if (open) {
      setCategory(defaultCategory || "");
      setFrom(defaultFrom || "");
      setReplyTo("");
      setSubject("");
      setBody("");
      setActuallySendEmails(false);
      setResult(null);
      setError("");
      setFollowUpAmount(3);
      setFollowUpUnit("day");
      setFollowUpText("");
      setCreatingFollowUps(false);
      setCreatingBulkEventReminder(false);
      setFollowUpStatus("");
      setSignatureBlocks(INITIAL_SIGNATURE_BLOCKS);
      setSelectedSignatureKey("signature_1");
      setLoadingSignatures(false);
      setStateFilter("Any");
      setDomainFilter("");
      setSearchInput("");
      setSearchFilter("");
    }
  }, [open, defaultCategory, defaultFrom]);

  // Load signatures from domain (read-only).
  useEffect(() => {
    if (!open || !domain) return undefined;
    let cancelled = false;
    setLoadingSignatures(true);

    (async () => {
      try {
        const domainRecord = await apiGetDomainByName(domain);
        if (cancelled || !domainRecord) return;

        const detailsObj = domainRecord?.details && typeof domainRecord.details === "object" ? domainRecord.details : null;
        const dataObj = domainRecord?.data && typeof domainRecord.data === "object" ? domainRecord.data : null;
        const sourceObj = detailsObj || dataObj || {};

        const stored = sourceObj?.bulkEmail?.signatures;
        const selectedKey = sourceObj?.bulkEmail?.selectedSignatureKey;
        const normalized = normalizeSignatureBlocks(stored);

        setSignatureBlocks(normalized);
        setSelectedSignatureKey(
          normalized.some((sig) => sig.key === selectedKey)
            ? selectedKey
            : (normalized[0]?.key || "signature_1")
        );
      } catch (err) {
        if (!cancelled) {
          console.error("Failed loading domain signatures:", err);
        }
      } finally {
        if (!cancelled) setLoadingSignatures(false);
      }
    })();

    return () => { cancelled = true; };
  }, [open, domain]);

  // Build the pool: either from explicit recipients or by fetching the category.
  useEffect(() => {
    if (!open) {
      setPool([]);
      setPoolLoading(false);
      return undefined;
    }

    // Explicit recipients path: normalize, skip API call.
    if (usingExplicitRecipients) {
      const normalized = (recipients || []).map(toRawContact).filter(Boolean);
      setPool(normalized);
      setPoolLoading(false);
      return undefined;
    }

    // Legacy: fetch by category.
    if (!category || !authorId) {
      setPool([]);
      setPoolLoading(false);
      return undefined;
    }
    let cancelled = false;
    setPoolLoading(true);
    (async () => {
      try {
        const { items } = await apiGetContactsCollection({
          // Eligibility checks read data.email / doNotContact flags -- needs
          // full documents, not the slim card fragment.
          full: true,
          authorId: null,
          pagination: { page: 1, limit: 1000 },
          ordering: { field: "updatedAt", order: "Desc" },
          filters: { category, ...(domain ? { domain } : {}) },
        });
        if (cancelled) return;
        setPool(items || []);
      } catch (err) {
        if (!cancelled) {
          console.error("Failed to load pool:", err);
          setPool([]);
        }
      } finally {
        if (!cancelled) setPoolLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, usingExplicitRecipients, recipients, category, domain, authorId]);

  // Apply filters on top of the pool.
  const filteredRecipients = useMemo(() => {
    let list = pool;
    if (stateFilter && stateFilter !== "Any") {
      const want = stateFilter.toLowerCase();
      list = list.filter((c) => String(c?.state || "").toLowerCase() === want);
    }
    if (isSuperAdmin && domainFilter.trim()) {
      const want = domainFilter.trim().toLowerCase();
      list = list.filter((c) => String(c?.domain || "").toLowerCase() === want);
    }
    if (searchFilter.trim()) {
      const q = searchFilter.trim().toLowerCase();
      list = list.filter((c) => {
        const fn = String(c?.identity?.person?.firstName || "").toLowerCase();
        const ln = String(c?.identity?.person?.lastName || "").toLowerCase();
        const co = String(c?.identity?.company?.name || "").toLowerCase();
        if (fn.includes(q) || ln.includes(q) || co.includes(q)) return true;
        const emails = Array.isArray(c?.reach?.emails) ? c.reach.emails : [];
        return emails.some((e) => String(e?.value || "").toLowerCase().includes(q));
      });
    }
    return list;
  }, [pool, stateFilter, domainFilter, searchFilter, isSuperAdmin]);

  const recipientsWithEmail = useMemo(
    () => filteredRecipients.filter(contactHasEmail),
    [filteredRecipients]
  );
  const doNotContactRecipients = useMemo(
    () => recipientsWithEmail.filter(isDoNotContact),
    [recipientsWithEmail]
  );
  const eligibleRecipients = useMemo(
    () => recipientsWithEmail.filter((contact) => !isDoNotContact(contact)),
    [recipientsWithEmail]
  );

  const canSend =
    (usingExplicitRecipients || !!category)
    && !!subject.trim()
    && !!body.trim()
    && !!from.trim()
    && !sending
    && !!authorId
    && eligibleRecipients.length > 0;

  const selectedSignature = useMemo(
    () => signatureBlocks.find((sig) => sig.key === selectedSignatureKey) || signatureBlocks[0] || null,
    [signatureBlocks, selectedSignatureKey]
  );
  const selectedSignatureToken = selectedSignature?.key ? `{{${selectedSignature.key}}}` : "";

  const insertAtCursor = (fieldId, token) => {
    const el = document.getElementById(fieldId);
    if (!el) {
      setBody((prev) => `${prev}${token}`);
      return;
    }
    const start = el.selectionStart ?? el.value.length;
    const end = el.selectionEnd ?? el.value.length;
    const next = `${body.slice(0, start)}${token}${body.slice(end)}`;
    setBody(next);
    setTimeout(() => {
      try {
        el.focus();
        const pos = start + token.length;
        el.setSelectionRange(pos, pos);
      } catch (_err) {
        // noop
      }
    }, 0);
  };

  const handleSend = async () => {
    if (!canSend) return;
    setSending(true);
    setError("");
    setResult(null);
    try {
      const hasGenericToken = body.includes("{{signature}}");
      const bodyWithSignatureToken = hasGenericToken
        ? body.replaceAll("{{signature}}", selectedSignatureToken || "")
        : `${body.trim()}\n\n${selectedSignatureToken || "{{signature_1}}"}`;

      if (usingExplicitRecipients) {
        if (!actuallySendEmails) {
          setResult(createDryRunResult(filteredRecipients, eligibleRecipients, doNotContactRecipients));
          return;
        }
        // Backend gap: apiSendEmailToCategory accepts only a category, not an
        // explicit recipient list. Stub the send and surface a clear message.
        console.warn(
          "[BulkEmailDialog] Explicit-recipients send not yet supported by backend. "
          + `Would have sent to ${recipientsWithEmail.length} recipient(s).`
        );
        setResult({
          totalMatched: filteredRecipients.length,
          totalSent: 0,
          totalFailed: 0,
          totalSkipped: filteredRecipients.length - recipientsWithEmail.length,
          results: [],
          _stub: true,
        });
        setError("Bulk send via explicit recipient list is queued for backend support — no emails were sent.");
        return;
      }

      if (!actuallySendEmails) {
        const out = await apiSendEmailToCategory({
          authorId,
          domain: domain || undefined,
          category,
          subject: subject.trim(),
          body: bodyWithSignatureToken,
          from: from.trim(),
          replyTo: replyTo.trim() || undefined,
          dryRun: true,
          ...DEFAULT_EMAIL_WORKFLOW_OPTIONS,
        });
        setResult(out || createDryRunResult(filteredRecipients, eligibleRecipients, doNotContactRecipients));
        return;
      }

      const out = await apiSendEmailToCategory({
        authorId,
        domain: domain || undefined,
        category,
        subject: subject.trim(),
        body: bodyWithSignatureToken,
        from: from.trim(),
        replyTo: replyTo.trim() || undefined,
        dryRun: false,
        ...DEFAULT_EMAIL_WORKFLOW_OPTIONS,
      });
      setResult(out);
    } catch (err) {
      console.error("Bulk send failed:", err);
      setError(err?.message || "Unknown error");
    } finally {
      setSending(false);
    }
  };

  const addDuration = (baseDate, amount, unit) => {
    const d = new Date(baseDate);
    const n = Number(amount);
    if (unit === "day") d.setDate(d.getDate() + n);
    if (unit === "week") d.setDate(d.getDate() + n * 7);
    if (unit === "month") d.setMonth(d.getMonth() + n);
    if (unit === "year") d.setFullYear(d.getFullYear() + n);
    return d;
  };

  const setTimeTo8am = (date) => {
    const d = new Date(date);
    d.setHours(8, 0, 0, 0);
    return d;
  };

  const handleCreateFollowUps = async () => {
    if (!result?.results?.length || !authorId || creatingFollowUps) return;
    if (result?.dryRun) {
      setFollowUpStatus("Dry run only: turn on actual sending before creating follow-up reminders.");
      return;
    }
    const sentRows = result.results.filter((r) => r.status === "sent" && r.contactId);
    if (!sentRows.length) {
      setFollowUpStatus("No sent contacts available for follow-up reminders.");
      return;
    }
    if (!followUpAmount || Number(followUpAmount) <= 0) {
      setFollowUpStatus("Please enter a valid follow-up time.");
      return;
    }

    setCreatingFollowUps(true);
    setFollowUpStatus("");
    try {
      const dueDate = setTimeTo8am(addDuration(new Date(), Number(followUpAmount), followUpUnit)).toISOString();
      const details = followUpText.trim()
        || `Follow up if no response to "${subject.trim() || "bulk email"}" (${category || "contact type"}).`;

      let created = 0;
      for (const row of sentRows) {
        try {
          await apiCreateJournal({
            authorId,
            ref: { collection: "contacts", id: row.contactId },
            type: "reminder",
            dueDate,
            data: {
              amount: Number(followUpAmount),
              unit: followUpUnit,
              action: "email",
              text: details,
              campaign: {
                category: category || "",
                subject: subject || "",
              },
            },
          });
          created += 1;
        } catch (_err) {
          // Continue creating reminders for other contacts.
        }
      }
      setFollowUpStatus(`Created ${created} follow-up reminder${created === 1 ? "" : "s"} for sent contacts.`);
    } catch (err) {
      setFollowUpStatus(err?.message || "Failed to create follow-up reminders.");
    } finally {
      setCreatingFollowUps(false);
    }
  };

  const handleCreateBulkEventReminder = async () => {
    if (creatingBulkEventReminder) return;
    setCreatingBulkEventReminder(true);
    try {
      window.dispatchEvent(new CustomEvent(OPEN_GLOBAL_REMINDER_EVENT, { detail: { source: "bulk_email" } }));
      setFollowUpStatus("Opened global reminder composer.");
      onClose?.();
    } finally {
      setCreatingBulkEventReminder(false);
    }
  };

  const headerTitle = title || (usingExplicitRecipients ? "Bulk Email" : "Bulk Email by Contact Type (AWS SES)");
  const noSignatures = !loadingSignatures && (signatureBlocks.length === 0 || signatureBlocks.every((s) => !s.value));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
      <Box sx={{ display: "flex", alignItems: "center", px: 2, py: 1, borderBottom: "1px solid #E5E7EB", backgroundColor: "#F9FAFB", gap: 1 }}>
        <AutoAwesomeIcon sx={{ fontSize: 18, color: "#7C3AED" }} />
        <Typography sx={{ flex: 1, fontSize: 13, fontWeight: 700, color: "#111827" }}>
          {headerTitle}
        </Typography>
        <UniversalActionButton
          styleType={UNIVERSAL_BUTTON_STYLES.STYLE1}
          label="Reminder"
          icon={<AddIcon fontSize="small" />}
          circleColor="#D97706"
          iconColor="#FFFFFF"
          labelColor="text.secondary"
          onClick={handleCreateBulkEventReminder}
          title="Create global reminder"
          aria-label="Create global reminder"
        />
        <IconButton size="small" onClick={onClose}>
          <IconClose sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>

      <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 1.25 }}>
        {!usingExplicitRecipients && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#475569", minWidth: 64 }}>Contact Type</Typography>
            <FormControl size="small" sx={{ flex: 1 }}>
              <Select
                value={category}
                displayEmpty
                onChange={(e) => setCategory(e.target.value)}
                renderValue={(selected) =>
                  selected ? selected : <span style={{ color: "#9CA3AF" }}>Select a contact type</span>
                }
                sx={{ backgroundColor: "#fff", fontSize: 13, "& .MuiSelect-select": { py: 0.75 } }}
              >
                <MenuItem value="" sx={{ fontSize: 13 }}>
                  <em>— pick a contact type —</em>
                </MenuItem>
                {categoryOptions.map((opt) => (
                  <MenuItem key={opt} value={opt} sx={{ fontSize: 13 }}>{opt}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        )}

        {/* Filters row */}
        <Box sx={{ p: 1.25, border: "1px solid #E2E8F0", borderRadius: 1.25, backgroundColor: "#FFFFFF" }}>
          <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#111827", mb: 0.75 }}>
            Recipients
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1 }}>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <Select
                value={stateFilter}
                onChange={(e) => setStateFilter(e.target.value)}
                sx={{ "& .MuiSelect-select": { fontSize: 13, py: 0.75 } }}
              >
                <MenuItem value="Any" sx={{ fontSize: 13 }}>Any state</MenuItem>
                <MenuItem value="Queued" sx={{ fontSize: 13 }}>Queued</MenuItem>
                <MenuItem value="Sent" sx={{ fontSize: 13 }}>Sent</MenuItem>
                <MenuItem value="Failed" sx={{ fontSize: 13 }}>Failed</MenuItem>
              </Select>
            </FormControl>
            {isSuperAdmin && (
              <TextField
                size="small"
                placeholder="Domain"
                value={domainFilter}
                onChange={(e) => setDomainFilter(e.target.value)}
                sx={{ width: 160, "& .MuiOutlinedInput-root": { fontSize: 13 } }}
              />
            )}
            <TextField
              size="small"
              placeholder="Search name, company, email…"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              sx={{ flex: 1, minWidth: 200, "& .MuiOutlinedInput-root": { fontSize: 13 } }}
            />
          </Box>
          <Typography sx={{ mt: 0.75, fontSize: 11, color: "#64748B" }}>
            {poolLoading
              ? "Loading recipients…"
              : `${filteredRecipients.length} recipient${filteredRecipients.length === 1 ? "" : "s"} match · ${eligibleRecipients.length} eligible · ${doNotContactRecipients.length} do-not-contact`}
          </Typography>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#475569", minWidth: 64 }}>From</Typography>
          <TextField
            size="small"
            fullWidth
            placeholder="verified-sender@yourdomain.com"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            sx={{ "& .MuiOutlinedInput-root": { fontSize: 13 } }}
          />
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#475569", minWidth: 64 }}>Reply-To</Typography>
          <TextField
            size="small"
            fullWidth
            placeholder="optional"
            value={replyTo}
            onChange={(e) => setReplyTo(e.target.value)}
            sx={{ "& .MuiOutlinedInput-root": { fontSize: 13 } }}
          />
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#475569", minWidth: 64 }}>Subject</Typography>
          <TextField
            size="small"
            fullWidth
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            sx={{ "& .MuiOutlinedInput-root": { fontSize: 13 } }}
          />
        </Box>

        <TextField
          id="bulk-email-body"
          multiline
          minRows={8}
          maxRows={16}
          fullWidth
          placeholder="Write your message…  (use {{firstName}}, {{lastName}}, {{fullName}}, {{company}}, {{signature}} to personalize)"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          sx={{ "& .MuiOutlinedInput-root": { fontSize: 13, lineHeight: 1.5 } }}
        />
        <Typography sx={{ fontSize: 11, color: "#64748B" }}>
          Tip: <code>{`{{firstName}}`}</code>, <code>{`{{lastName}}`}</code>, <code>{`{{fullName}}`}</code>, <code>{`{{company}}`}</code>, <code>{`{{email}}`}</code>, <code>{`{{signature}}`}</code> are substituted per recipient. Each email is addressed as <code>&quot;Full Name&quot; &lt;email&gt;</code>.
        </Typography>

        <Box sx={{ p: 1.25, border: "1px solid #E2E8F0", borderRadius: 1.25, backgroundColor: actuallySendEmails ? "#FFF7ED" : "#F8FAFC" }}>
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={actuallySendEmails}
                onChange={(e) => setActuallySendEmails(e.target.checked)}
              />
            }
            label={
              <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#111827" }}>
                Actually send emails
              </Typography>
            }
            sx={{ m: 0 }}
          />
          <Typography sx={{ mt: 0.5, fontSize: 11, color: "#64748B" }}>
            {actuallySendEmails
              ? "Live mode: this will call the email sender for every matching recipient with an email."
              : "Dry run: validates the workflow and shows the send summary without sending emails."}
          </Typography>
        </Box>

        {/* Signature selection (read-only — editing lives in Company popup) */}
        <Box sx={{ p: 1.25, border: "1px solid #E2E8F0", borderRadius: 1.25, backgroundColor: "#FFFFFF" }}>
          <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#111827", mb: 0.75 }}>
            Signature
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography sx={{ fontSize: 12, color: "#475569", minWidth: 64 }}>Use</Typography>
            <FormControl size="small" sx={{ minWidth: 220 }} disabled={loadingSignatures || signatureBlocks.length === 0}>
              <Select
                value={selectedSignatureKey}
                onChange={(e) => setSelectedSignatureKey(e.target.value)}
                sx={{ "& .MuiSelect-select": { fontSize: 13, py: 0.75 } }}
              >
                {signatureBlocks.map((sig) => (
                  <MenuItem key={sig.key} value={sig.key} sx={{ fontSize: 13 }}>
                    {sig.key} ({sig.label || sig.key})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              size="small"
              variant="outlined"
              onClick={() => insertAtCursor("bulk-email-body", "{{signature}}")}
              sx={{ textTransform: "none", fontSize: 12 }}
            >
              {"Insert {{signature}}"}
            </Button>
          </Box>
          <Typography sx={{ mt: 0.75, fontSize: 11, color: "#64748B" }}>
            {loadingSignatures
              ? "Loading signatures from domain…"
              : (noSignatures
                ? "No signatures yet — add them in Company → Signature Blocks."
                : (<>When sending, <code>{`{{signature}}`}</code> is converted to <code>{selectedSignatureToken || "{{signature_1}}"}</code>.</>))}
          </Typography>
        </Box>

        {error && (
          <Typography sx={{ fontSize: 12, color: "#DC2626" }}>
            {error}
          </Typography>
        )}

        {result && (
          <Box sx={{ p: 1, border: "1px solid #E5E7EB", borderRadius: 1, backgroundColor: "#F9FAFB" }}>
            <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#111827", mb: 0.5 }}>
              {result.dryRun ? "Dry Run Result" : "Result"}
            </Typography>
            <Typography sx={{ fontSize: 12, color: "#374151" }}>
              Matched: {result.totalMatched} · Attempted: {result.totalAttempted || 0} · Sent: {result.totalSent} · Failed: {result.totalFailed} · Skipped: {result.totalSkipped}
            </Typography>
            {result.dryRun && (
              <Typography sx={{ mt: 0.5, fontSize: 11, color: "#166534" }}>
                No emails were sent. Turn on actual sending to dispatch this email.
              </Typography>
            )}
            {result.totalFailed > 0 && (
              <Box sx={{ mt: 0.5, maxHeight: 120, overflowY: "auto" }}>
                {result.results
                  .filter((r) => r.status === "failed")
                  .slice(0, 10)
                  .map((r, i) => (
                    <Typography key={i} sx={{ fontSize: 11, color: "#7F1D1D" }}>
                      {r.email}: {r.error}
                    </Typography>
                  ))}
              </Box>
            )}
          </Box>
        )}

        {result?.totalSent > 0 && (
          <Box sx={{ p: 1.25, border: "1px solid #E2E8F0", borderRadius: 1.25, backgroundColor: "#FFFFFF" }}>
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#111827", mb: 0.75 }}>
              Follow-up reminder (if no response)
            </Typography>
            <Divider sx={{ mb: 1 }} />
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <Typography sx={{ fontSize: 12, color: "#475569", minWidth: 64 }}>In</Typography>
              <TextField
                size="small"
                type="number"
                value={followUpAmount}
                onChange={(e) => setFollowUpAmount(e.target.value)}
                sx={{ width: 88, "& .MuiOutlinedInput-root": { fontSize: 13 } }}
              />
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <Select
                  value={followUpUnit}
                  onChange={(e) => setFollowUpUnit(e.target.value)}
                  sx={{ "& .MuiSelect-select": { fontSize: 13, py: 0.75 } }}
                >
                  <MenuItem value="day">day(s)</MenuItem>
                  <MenuItem value="week">week(s)</MenuItem>
                  <MenuItem value="month">month(s)</MenuItem>
                  <MenuItem value="year">year(s)</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <TextField
              size="small"
              fullWidth
              multiline
              minRows={2}
              placeholder="Optional reminder text (defaults to email follow-up message)"
              value={followUpText}
              onChange={(e) => setFollowUpText(e.target.value)}
              sx={{ "& .MuiOutlinedInput-root": { fontSize: 13 } }}
            />
            {followUpStatus && (
              <Typography sx={{ mt: 0.75, fontSize: 11, color: followUpStatus.startsWith("Created") || followUpStatus.startsWith("Saved") ? "#166534" : "#B45309" }}>
                {followUpStatus}
              </Typography>
            )}
            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
              <Box sx={{ display: "flex", gap: 0.75 }}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={handleCreateFollowUps}
                  disabled={creatingFollowUps}
                  sx={{ textTransform: "none", fontSize: 12 }}
                >
                  {creatingFollowUps ? "Creating reminders…" : "Create follow-up reminders"}
                </Button>
              </Box>
            </Box>
          </Box>
        )}

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 0.5 }}>
          <Button size="small" onClick={onClose} sx={{ textTransform: "none", fontSize: 12 }}>
            Close
          </Button>
          <Button
            size="small"
            variant="contained"
            startIcon={sending ? <CircularProgress size={12} sx={{ color: "#fff" }} /> : <IconSend sx={{ fontSize: 14 }} />}
            onClick={handleSend}
            disabled={!canSend}
            sx={{
              textTransform: "none", fontSize: 12, fontWeight: 600,
              backgroundColor: "#7C3AED", "&:hover": { backgroundColor: "#6D28D9" },
            }}
          >
            {sending
              ? (actuallySendEmails ? "Sending..." : "Running...")
              : (actuallySendEmails
                ? (usingExplicitRecipients ? "Send to recipients" : "Send to contact type")
                : "Run dry run")}
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};

// Backwards-compatible alias — many callers still import by the old name.
const SendEmailToCategoryDialog = BulkEmailDialog;

export { BulkEmailDialog };
export default SendEmailToCategoryDialog;
